class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleEscapeKeyUp = (evt) => evt.code === 'Escape' && this.close();
    this.handleOverlayClick = this.close.bind(this);
    this.handleCartIconClick = this.onCartIconClick.bind(this);
    this.handleCartIconKeydown = this.onCartIconKeydown.bind(this);
    this.handleDrawerLinkClick = this.onDrawerLinkClick.bind(this);

    this.addEventListener('keyup', this.handleEscapeKeyUp);
    this.addEventListener('click', this.handleDrawerLinkClick);
    this.bindOverlayClick();
    this.bindCartIconTrigger();
  }

  bindOverlayClick() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    overlay.removeEventListener('click', this.handleOverlayClick);
    overlay.addEventListener('click', this.handleOverlayClick);
  }

  onCartIconClick(event) {
    event.preventDefault();
    this.open(event.currentTarget);
  }

  onCartIconKeydown(event) {
    const key = (event.code || event.key || '').toUpperCase();
    if (key !== 'ENTER' && key !== 'SPACE' && key !== ' ') return;
    event.preventDefault();
    this.open(event.currentTarget);
  }

  onDrawerLinkClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor) return;
    if (!this.contains(anchor) || !anchor.closest('#CartDrawer')) return;

    if (anchor.hasAttribute('data-no-close')) return;
    if (anchor.closest('cart-remove-button')) return;
    if (anchor.closest('quantity-input')) return;
    if (anchor.classList.contains('cart__checkout-button') || anchor.getAttribute('name') === 'checkout') return;

    const target = (anchor.getAttribute('target') || '').toLowerCase();
    if (target === '_blank') return;
    if (anchor.hasAttribute('download')) return;

    const href = (anchor.getAttribute('href') || '').trim();
    if (!href || href === '#' || href.startsWith('#')) return;
    if (href.toLowerCase().startsWith('javascript:')) return;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (e) {
      return;
    }

    if (url.origin !== window.location.origin) return;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

    this.close();
  }

  bindCartIconTrigger() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    if (this.boundCartLink && this.boundCartLink !== cartLink) {
      this.boundCartLink.removeEventListener('click', this.handleCartIconClick);
      this.boundCartLink.removeEventListener('keydown', this.handleCartIconKeydown);
      delete this.boundCartLink.dataset.cartDrawerBound;
    }

    if (cartLink.dataset.cartDrawerBound === 'true') {
      this.boundCartLink = cartLink;
      return;
    }

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', this.handleCartIconClick);
    cartLink.addEventListener('keydown', this.handleCartIconKeydown);
    cartLink.dataset.cartDrawerBound = 'true';
    this.boundCartLink = cartLink;
  }

  disconnectedCallback() {
    this.removeEventListener('keyup', this.handleEscapeKeyUp);
    this.removeEventListener('click', this.handleDrawerLinkClick);
    if (this.boundCartLink) {
      this.boundCartLink.removeEventListener('click', this.handleCartIconClick);
      this.boundCartLink.removeEventListener('keydown', this.handleCartIconKeydown);
      delete this.boundCartLink.dataset.cartDrawerBound;
    }
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.removeEventListener('click', this.handleOverlayClick);
  }

  setHeaderCartIconAccessibility() {
    this.bindCartIconTrigger();
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.removeAttribute('hidden');
    this.primeDrawerItemImages();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('active');

    const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    trapFocus(containerToTrapFocusOn, focusElement);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));
    this.primeDrawerItemImages();

    // Rebind handlers after cart icon section replacement.
    this.bindOverlayClick();
    this.bindCartIconTrigger();

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  primeDrawerItemImages() {
    const maxToPrime = 2;
    const drawerRoot = this.querySelector('#CartDrawer') || this;
    const images = Array.from(drawerRoot.querySelectorAll('.cart-item__image')).slice(0, maxToPrime);

    images.forEach((img, index) => {
      if (!img || img.dataset.drawerImagePrimed === 'true') return;

      img.setAttribute('loading', 'eager');
      if (index === 0) {
        img.setAttribute('fetchpriority', 'high');
      }

      const src = img.currentSrc || img.getAttribute('src');
      if (src) {
        const warm = new Image();
        warm.decoding = 'async';
        warm.src = src;
      }

      if (typeof img.decode === 'function' && !(img.complete && img.naturalWidth > 0)) {
        img.decode().catch(() => {});
      }

      img.dataset.drawerImagePrimed = 'true';
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
